import React, { useMemo } from 'react';

/**
 * ScreenPreview - Shows how content will look on the target screen.
 * Renders a scaled mockup of the screen at its native aspect ratio,
 * with the uploaded content displayed inside.
 *
 * When a `calibration` prop is provided (from the device's saved
 * calibration profile), the preview simulates physical display
 * distortion:
 *   - Overscan: CSS clip-path inset crops the edges
 *   - RGB gains: inline SVG feColorMatrix adjusts per-channel color
 *   - Brightness/Contrast/Saturation: CSS filter functions
 *
 * @param {Object} props
 * @param {number} props.screenWidth - Target screen native width
 * @param {number} props.screenHeight - Target screen native height
 * @param {string|File} props.media - URL string or File object to preview
 * @param {string} [props.mediaType] - 'image' or 'video' (auto-detected if File)
 * @param {number} [props.maxPreviewHeight=280] - Max height of the preview frame in px
 * @param {Object} [props.calibration] - Device calibration profile
 */
const ScreenPreview = ({ screenWidth, screenHeight, media, mediaType, maxPreviewHeight = 280, calibration }) => {
  const mediaUrl = useMemo(() => {
    if (!media) return null;
    if (typeof media === 'string') return media;
    if (media instanceof File) return URL.createObjectURL(media);
    return null;
  }, [media]);

  const isVideo = useMemo(() => {
    if (mediaType === 'video') return true;
    if (media instanceof File) return media.type?.startsWith('video/');
    if (typeof media === 'string') return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(media);
    return false;
  }, [media, mediaType]);

  // Scale the screen frame to fit within maxPreviewHeight
  const { frameWidth, frameHeight } = useMemo(() => {
    if (!screenWidth || !screenHeight) return { frameWidth: 200, frameHeight: 280 };
    const aspect = screenWidth / screenHeight;
    let h = maxPreviewHeight;
    let w = h * aspect;
    // Clamp width to a reasonable max (400px)
    if (w > 400) {
      w = 400;
      h = w / aspect;
    }
    return { frameWidth: Math.round(w), frameHeight: Math.round(h) };
  }, [screenWidth, screenHeight, maxPreviewHeight]);

  // Build calibration CSS from the device's calibration profile
  const calibrationStyles = useMemo(() => {
    if (!calibration) return { filter: 'none', clipPath: 'none', svgFilterId: null };

    const parts = [];

    // Brightness/contrast/saturation from CSS filter
    const c = calibration.color;
    if (c) {
      if (c.brightness != null && c.brightness !== 0) parts.push(`brightness(${1 + c.brightness})`);
      if (c.contrast != null && c.contrast !== 1) parts.push(`contrast(${c.contrast})`);
      if (c.saturation != null && c.saturation !== 1) parts.push(`saturate(${c.saturation})`);
    }

    // RGB gain needs SVG feColorMatrix — we'll reference it via url(#...)
    const hasRgbGain = c && (
      (c.redGain != null && c.redGain !== 1) ||
      (c.greenGain != null && c.greenGain !== 1) ||
      (c.blueGain != null && c.blueGain !== 1)
    );
    const svgFilterId = hasRgbGain ? 'screen-preview-rgb-gain' : null;
    if (svgFilterId) {
      parts.unshift(`url(#${svgFilterId})`);
    }

    // Overscan as clip-path inset
    const os = calibration.overscan;
    let clipPath = 'none';
    if (os && (os.top > 0 || os.right > 0 || os.bottom > 0 || os.left > 0)) {
      clipPath = `inset(${os.top || 0}% ${os.right || 0}% ${os.bottom || 0}% ${os.left || 0}%)`;
    }

    return {
      filter: parts.length > 0 ? parts.join(' ') : 'none',
      clipPath,
      svgFilterId,
    };
  }, [calibration]);

  if (!screenWidth || !screenHeight) return null;

  const hasCalibration = calibration && (
    calibrationStyles.filter !== 'none' ||
    calibrationStyles.clipPath !== 'none'
  );

  const c = calibration?.color;

  return (
    <div style={containerStyle}>
      <div
        style={{
          ...frameStyle,
          width: frameWidth,
          height: frameHeight,
        }}
      >
        {/* Inline SVG filter for RGB gain — only mounted when needed */}
        {calibrationStyles.svgFilterId && (
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <filter id={calibrationStyles.svgFilterId}>
                <feColorMatrix
                  type="matrix"
                  values={`
                    ${c?.redGain ?? 1} 0 0 0 0
                    0 ${c?.greenGain ?? 1} 0 0 0
                    0 0 ${c?.blueGain ?? 1} 0 0
                    0 0 0 1 0
                  `}
                />
              </filter>
            </defs>
          </svg>
        )}

        {mediaUrl ? (
          isVideo ? (
            <video
              src={mediaUrl}
              style={{
                ...mediaStyle,
                filter: calibrationStyles.filter,
                clipPath: calibrationStyles.clipPath,
              }}
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <img
              src={mediaUrl}
              alt="Content preview"
              style={{
                ...mediaStyle,
                filter: calibrationStyles.filter,
                clipPath: calibrationStyles.clipPath,
              }}
            />
          )
        ) : (
          <div style={placeholderStyle}>
            <span style={{ fontSize: 32, opacity: 0.3 }}>&#9634;</span>
            <span style={{ fontSize: 11, opacity: 0.5 }}>No content selected</span>
          </div>
        )}
      </div>
      <div style={labelStyle}>
        {screenWidth}x{screenHeight} preview
        {hasCalibration && (
          <span style={{ color: '#d97706', marginLeft: 4, fontSize: 10 }}>
            (calibrated)
          </span>
        )}
      </div>
    </div>
  );
};

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
};

const frameStyle = {
  background: '#000',
  borderRadius: 6,
  border: '3px solid #374151',
  overflow: 'hidden',
  position: 'relative',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
};

const mediaStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'fill',
  display: 'block',
};

const placeholderStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#9ca3af',
};

const labelStyle = {
  fontSize: 11,
  color: '#6b7280',
  fontWeight: 500,
};

export default ScreenPreview;
